import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import {
  clearPatientContactsData,
  doDeletePatientContact,
  doFetchPatientContacts,
} from '@state/reducers/patientContactReducer'
import { useEffect, useMemo, useState } from 'react'
import { DataGridPro, GridColumns } from '@mui/x-data-grid-pro'
import {
  Box,
  Chip,
  IconButton,
  Dialog,
  DialogContent,
  Typography,
  Button,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/DeleteForever'
import AddIcon from '@mui/icons-material/Add'
import {
  PatientContactDto,
  PatientContactRelationship,
} from '@services/patientContactService'
import PatientContactForm from './forms/PatientContactForm'
import { UpTransition } from '@components/animations'
import { RELATIONSHIP_LABELS } from '@utils/constants'
import { Phone, WhatsApp } from '@mui/icons-material'
import moment from 'moment'
import { EditIcon, Trash2 } from 'lucide-react'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { DeleteConfirmationDialog } from './dialogs/DeleteConfirmationDialog'

interface Props {
  patientId?: number
}

const PatientContactInfos = ({ patientId }: Props) => {
  const dispatch = useAppDispatch()
  const { patientContacts, loading } = useAppSelector(
    (state) => state.patientContact,
  )

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [editingContact, setEditingContact] =
    useState<PatientContactDto | null>(null)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)

  useEffect(() => {
    if (patientId) {
      dispatch(doFetchPatientContacts(patientId))
    } else {
      dispatch(clearPatientContactsData())
    }

    return () => {
      dispatch(clearPatientContactsData())
    }
  }, [patientId, dispatch])

  const handleDeleteConfirm = async (id?: number) => {
    if (!id) {
      return
    }
    try {
      await dispatch(doDeletePatientContact(id)).unwrap()
      dispatch(
        enqueueSnackbar({
          message: 'Contact supprimé avec succès',
          type: 'success',
        }),
      )
    } catch (error) {
      console.error(error)
      dispatch(
        enqueueSnackbar({
          message: 'Une erreur est survenue lors de la suppression du contact',
          type: 'error',
        }),
      )
    } finally {
      setIsDeleteDialogOpen(false)
      setEditingContact(null)
    }
  }

  const columns = useMemo<GridColumns>(
    () => [
      {
        field: 'firstName',
        headerName: 'Prénom',
        width: 120,
      },
      {
        field: 'lastName',
        headerName: 'Nom',
        width: 120,
      },
      {
        field: 'dateOfBirth',
        headerName: 'Date de naissance',
        width: 150,
        type: 'date',
        valueGetter: (params) => {
          if (!params.value) return '-'
          return moment(params.value).format('DD-MM-YYYY')
        },
      },
      {
        field: 'relationshipType',
        headerName: 'Relation',
        width: 100,
        renderCell: (params) => (
          <Chip
            label={
              RELATIONSHIP_LABELS[params.value as PatientContactRelationship] ||
              params.value
            }
            size="small"
            color="info"
            variant="filled"
          />
        ),
      },
      {
        field: 'phone',
        headerName: 'Téléphone',
        width: 150,
        renderCell: (params) => {
          const phone = params.row.phone
          const whatsapp = params.row.whatsapp
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              {phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Phone fontSize="inherit" />
                  <Typography variant="body2">{phone}</Typography>
                </Box>
              )}
              {whatsapp && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <WhatsApp fontSize="inherit" color="success" />
                  <Typography variant="body2">{whatsapp}</Typography>
                </Box>
              )}
              {!phone && !whatsapp && (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              )}
            </Box>
          )
        },
      },
      {
        field: 'email',
        headerName: 'Email',
        width: 280,
        flex: 1,
        valueGetter: (params) => {
          if (!params.value) return '-'
          return params.value
        },
      },
      {
        field: 'isLegalGuardian',
        headerName: 'Représentant légal',
        width: 180,
        type: 'boolean',
      },
      {
        field: 'canRequestReport',
        headerName: 'Dem. compte rendu',
        width: 200,
        type: 'boolean',
      },
      {
        field: 'is2fa',
        headerName: '2FA',
        width: 80,
        type: 'boolean',
      },
      {
        field: 'actions',
        headerName: 'Actions',
        headerAlign: 'right',
        align: 'right',
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <Box>
            <IconButton
              size="small"
              onClick={() => {
                setEditingContact(params.row as PatientContactDto)
                setIsFormOpen(true)
              }}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setEditingContact(params.row)
                setIsDeleteDialogOpen(true)
              }}
            >
              <Trash2 />
            </IconButton>
          </Box>
        ),
      },
    ],
    [],
  )

  const handleCloseForm = () => {
    setIsFormOpen(false)
    setEditingContact(null)

    if (patientId) {
      dispatch(doFetchPatientContacts(patientId)).unwrap()
    }
  }

  const handleAddContact = () => {
    setEditingContact(null)
    setIsFormOpen(true)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: 700,
        paddingTop: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Contacts du patient
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddContact}
          size="small"
        >
          Ajouter un contact
        </Button>
      </Box>

      {patientContacts.datas?.length ? (
        <DataGridPro
          rows={patientContacts.datas}
          columns={columns}
          loading={loading}
          pagination
          paginationMode="server"
          disableSelectionOnClick
          page={page}
          pageSize={pageSize}
          rowCount={patientContacts?.total || 0}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8f9fa',
            },
          }}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50%',
          }}
        >
          <Typography variant="body1">Aucun contact trouvé</Typography>
        </Box>
      )}

      <Dialog
        fullScreen
        open={isFormOpen}
        onClose={handleCloseForm}
        TransitionComponent={UpTransition}
      >
        <DialogContent>
          {isFormOpen && patientId && (
            <PatientContactForm
              patientId={patientId}
              editingContact={editingContact}
              onClose={handleCloseForm}
            />
          )}
        </DialogContent>
      </Dialog>

      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={() => handleDeleteConfirm(editingContact?.id)}
        title={`Supprimer le contact ${editingContact?.firstName} ${editingContact?.lastName} ?`}
        description="Êtes-vous sûr de vouloir supprimer ce contact ?"
      />
    </Box>
  )
}

export default PatientContactInfos
